import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import useIntroAnimation from '../hooks/useIntroAnimation'

type Candle = { id: number; open: number; close: number; high: number; low: number }

/**
 * Candles are keyed by identity, not array position. Keying by index would let
 * React repurpose an element for its neighbour's data when the series shifts,
 * and the transition below would animate that swap — making the whole chart
 * slide sideways instead of stepping over by one candle.
 */
let nextCandleId = 0

// Acme's splash is phone-sized; the proportions are kept but scaled ~1.7x so the
// ticker holds its own on a desktop viewport instead of reading as a stray mark.
const CANDLE_COUNT = 9
const CANDLE_WIDTH = 13
const CANDLE_GAP = 7
const WICK_WIDTH = 2
const MAX_HEIGHT = 100
const TRACK_WIDTH = (CANDLE_WIDTH + CANDLE_GAP) * CANDLE_COUNT

const UPDATE_INTERVAL = 70
const TICKS_PER_CANDLE = 5
const MAX_TICK = 13
const MIN_TICK = 7

/** Oldest candle's opacity. Anything lower disappears against the page background. */
const TRAIL_OPACITY = 0.3

/** Fraction of the track from either edge where the walk is nudged back toward the middle. */
const REVERSAL_ZONE = 0.35

const clampToTrack = (value: number) => Math.max(Math.min(value, MAX_HEIGHT), 0)

/** Normalizes a candle so its wick always spans at least its body. */
function withConsistentWick(candle: Candle): Candle {
  return {
    ...candle,
    high: Math.max(candle.high, candle.open, candle.close),
    low: Math.min(candle.low, candle.open, candle.close),
  }
}

/** Opens a flat candle at the previous candle's close, so the series stays continuous. */
function openCandle(prev?: Candle): Candle {
  const open = prev ? prev.close : Math.ceil(Math.random() * MAX_HEIGHT)
  return { id: nextCandleId++, open, close: open, high: open, low: open }
}

/** Builds an already-closed candle, used to fill the track before the first tick. */
function seedCandle(prev?: Candle): Candle {
  const candle = openCandle(prev)
  const bodySize = Math.ceil(Math.random() * (MAX_HEIGHT * 0.4))
  const wickSize = Math.ceil(Math.random() * 3)

  if (bodySize > MAX_HEIGHT * 0.2) {
    candle.close = Math.min(candle.open + bodySize, MAX_HEIGHT)
    candle.high = clampToTrack(candle.close + wickSize)
  } else {
    candle.close = Math.abs(candle.open - bodySize)
    candle.low = clampToTrack(candle.close - wickSize)
  }

  return withConsistentWick(candle)
}

/**
 * Walks the open candle one tick. `trend` biases the direction so the series
 * stays on the track instead of pinning to an edge: ±1 is a gentle pull toward
 * the middle, ±2 means an edge is close and the step size drops.
 */
function tickCandle(candle: Candle, trend: number): { candle: Candle; trend: number } {
  const rising = Math.ceil(Math.random() * 10) > 5 + trend
  const movement = Math.abs(trend) === 2 ? MIN_TICK : MAX_TICK
  const close = candle.close + (rising ? movement : -movement)

  let nextTrend = close > MAX_HEIGHT / 2 ? -1 : 1
  if (close > MAX_HEIGHT * (1 - REVERSAL_ZONE)) {
    nextTrend = 2
  } else if (close < MAX_HEIGHT * REVERSAL_ZONE) {
    nextTrend = -2
  }

  return {
    candle: withConsistentWick({ ...candle, close: clampToTrack(close) }),
    trend: nextTrend,
  }
}

/**
 * Boot renders LoadingShell twice in sequence — once from the route guard while
 * auth resolves, then again from ApplicationSuiteProvider while usermeta and
 * product tags load. Caching the series at module scope lets the ticker survive
 * that unmount/remount instead of visibly reseeding.
 */
const tickerCache: { candles: Candle[] | null; trend: number; tick: number } = {
  candles: null,
  trend: 0,
  tick: 0,
}

function seedCandles(): Candle[] {
  if (tickerCache.candles) return tickerCache.candles

  const seeded: Candle[] = []
  for (let i = 0; i < CANDLE_COUNT; i++) {
    seeded.push(seedCandle(seeded[seeded.length - 1]))
  }

  tickerCache.candles = seeded
  return seeded
}

type CandleLoaderProps = {
  /** Optional caption rendered under the ticker. Omitted by default — the logo carries identity. */
  message?: string
}

export default function CandleLoader({ message }: CandleLoaderProps) {
  const theme = useTheme()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const playIntro = useIntroAnimation('candleLoader') && !prefersReducedMotion

  const [candles, setCandles] = useState<Candle[]>(seedCandles)
  const trendRef = useRef(tickerCache.trend)
  const tickRef = useRef(tickerCache.tick)

  useEffect(() => {
    if (prefersReducedMotion) return

    // All bookkeeping happens here rather than inside a setCandles updater.
    // React may invoke an updater more than once, and this advance is not
    // idempotent: a replayed call would see the already-reset tick counter and
    // take the wrong branch, discarding the shift.
    const interval = setInterval(() => {
      const prev = tickerCache.candles ?? seedCandles()
      let next: Candle[]

      if (tickRef.current + 1 > TICKS_PER_CANDLE) {
        tickRef.current = 0
        const shifted = prev.slice(1)
        next = [...shifted, openCandle(shifted[shifted.length - 1])]
      } else {
        tickRef.current += 1
        const ticked = tickCandle(prev[prev.length - 1], trendRef.current)
        trendRef.current = ticked.trend
        next = [...prev.slice(0, -1), ticked.candle]
      }

      tickerCache.candles = next
      tickerCache.trend = trendRef.current
      tickerCache.tick = tickRef.current
      setCandles(next)
    }, UPDATE_INTERVAL)

    return () => clearInterval(interval)
  }, [prefersReducedMotion])

  const candleColor = (candle: Candle) => {
    if (candle.close === candle.open) return theme.palette.text.secondary
    return candle.close > candle.open ? theme.palette.primary.main : theme.palette.danger.main
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: TRACK_WIDTH,
          height: MAX_HEIGHT,
          '@keyframes candleLoaderRise': {
            from: { opacity: 0, transform: 'translateY(6px) scale(0.97)' },
            to: { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          opacity: playIntro ? 0 : 1,
          animation: playIntro ? 'candleLoaderRise 0.5s ease-out 0.1s forwards' : 'none',
        }}
      >
        {candles.map((candle, index) => {
          // Geometry is recomputed every tick, so it goes through `style` rather
          // than `sx` — emotion would otherwise reserialize every candle ~14x/sec.
          const color = candleColor(candle)
          const opacity = TRAIL_OPACITY + (1 - TRAIL_OPACITY) * (index / (CANDLE_COUNT - 1))
          const left = (CANDLE_WIDTH + CANDLE_GAP) * index
          const transition = prefersReducedMotion
            ? undefined
            : 'top 0.08s linear, height 0.08s linear'

          return (
            <React.Fragment key={candle.id}>
              {/* Wick first so the body paints over it */}
              <Box
                style={{
                  position: 'absolute',
                  left: left + (CANDLE_WIDTH - WICK_WIDTH) / 2,
                  top: MAX_HEIGHT - candle.high,
                  width: WICK_WIDTH,
                  height: Math.max(candle.high - candle.low, 1),
                  backgroundColor: color,
                  opacity,
                  transition,
                }}
              />
              <Box
                style={{
                  position: 'absolute',
                  left,
                  top: MAX_HEIGHT - Math.max(candle.open, candle.close),
                  width: CANDLE_WIDTH,
                  height: Math.max(Math.abs(candle.open - candle.close), 1),
                  borderRadius: 2,
                  backgroundColor: color,
                  opacity,
                  transition,
                }}
              />
            </React.Fragment>
          )
        })}
      </Box>

      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          {message}
        </Typography>
      )}
    </Box>
  )
}
